// Components/Tabs/ManageMachineTypesTab.jsx

import React, { useState, useCallback, useRef } from "react";
import PropTypes from "prop-types";



// Importing components
import Slider from "../Slider/Slider";
import DataTable from "../DataTable/DataTable";
import ResourceTabs from "../ResourceTabs/ResourceTabs";
import Backend from "../../../utils/api.js";
import ArchiveResourceDialog from "../ArchiveResourceDialog";
import AdminState from "../AdminState";
import { useDocumentTitle } from "../../../utils/useDocumentTitle";

/**
 * ManageMachineTypesTab Component
 * 
 * This component allows administrators to manage machine types within a lab. It displays
 * a data table of machine types with options to add or edit existing types.
 * 
 * @param {Object} props - Component properties.
 * @param {Object} props.user - The current user object containing user details.
 * @param {string} props.labId - The unique identifier of the lab.
 * @param {Function} props.pushTab - Function to push a new tab onto the stack.
 * @param {Function} props.revert - Function to revert the tab stack by a specified number of steps.
 */
function ManageMachineTypesTab({ user, labId, pushTab, revert }) {
  useDocumentTitle("Admin: Manage tools Tab – Pinpoint");

  const [refreshToggle, setRefreshToggle] = useState(false);
  
  const [machineTypeToArchive, setMachineTypeToArchive] = useState(null);
  const activeTabRef = useRef(null);
  const returnFocusRef = useRef(null);
  const fetchAllMachineTypes = useCallback(() => Backend.getMachineTypes(user.UID, 1000), [user.UID]);
  const loadAffected = useCallback(async () => {
    const response = await Backend.getMachineByLab(user.UID, labId);
    return { ...response, items: response.items?.filter(instance =>
      String(instance.machineType) === String(machineTypeToArchive?.id)) };
  }, [user.UID, labId, machineTypeToArchive]);

  // Function to fetch active machine types
  const activeDataFunction = useCallback(
    () =>
      fetchAllMachineTypes().then((res) => {
        if (res.error || !res.items) return { items: [], status: 0 };
        return { ...res, items: res.items.filter((m) => !Number(m.isDeleted)) };
      }),
    [fetchAllMachineTypes, refreshToggle]
  );

  // Function to fetch archived machine types
  const archivedDataFunction = useCallback(
    () =>
      Backend.getMachineTypes(user.UID, 1000, true).then((res) => {
        if (res.error || !res.items) return { items: [], status: 0 };
        return { ...res, items: res.items.filter((m) => Number(m.isDeleted)) };
      }),
    [user.UID, refreshToggle]
  );

  return (
    <Slider
      name="Manage tools"
      navColor="rgb(255, 153, 204)"
      slide={true} 
      index={0} // Starting index for the manage machine types tab
      onClick={() => revert(2)} // Revert two steps when navigation bar is clicked
    >
      <AdminState status="info" role="note" aria-live="off"
        title="Set up tools by make and model"
        description="A tool represents a make and model, such as Ultimaker S7. Reuse an existing tool when the same model is used in another space. Link credentials to the tool in Badge requirements, then add a tool instance for each physical unit. This keeps credential requirements consistent across spaces."
      />
      {/* ACTIVE / MAIN machine types */}
      <ResourceTabs activeTabRef={activeTabRef}>
      <DataTable
        title="Tools"
        columns={[
          { title: "ID", field: "id", editable: 'never', width: 72, minWidth: 72, maxWidth: 72, cellStyle: { width: 72 }, headerStyle: { width: 72 } },
          { title: "Tool", field: "name", cellStyle: { width: '100%' } },
        ]}
        dataFunction={activeDataFunction}
        submitRowChange={(newMachineType, oldMachineType) =>
          Backend.updateMachineType(
            user.UID,
            oldMachineType.id,
            newMachineType.name
          ) // Handle row updates
        }
        submitNewRow={(newMachineType) =>
          Backend.newMachineType(
            user.UID,
            newMachineType.id,
            newMachineType.name
          ) // Handle adding new machine type
        }
        key="active"
        customActions={[
          (rowData) => ({
            icon: 'archive',
            tooltip: 'Archive tool',
            onClick: (event) => {
              returnFocusRef.current = event.currentTarget;
              setMachineTypeToArchive(rowData);
            },
            hidden: false,
          }),
        ]}
        errorMsg="Action failed. Double check you aren't using a duplicate tool name."
      />

      {/* ARCHIVED machine types */}
      <DataTable
        title="Archived tools"
        columns={[
          { title: "ID", field: "id", editable: 'never', width: 72, minWidth: 72, maxWidth: 72, cellStyle: { width: 72 }, headerStyle: { width: 72 } },
          { title: "Tool", field: "name", cellStyle: { width: '100%' } },
        ]}
        dataFunction={archivedDataFunction}
        submitRowChange={(newMachineType, oldMachineType) =>
          Backend.updateMachineType(
            user.UID,
            oldMachineType.id,
            newMachineType.name
          )
        }
        key="archived"
        customActions={[
          (rowData) => ({
            icon: 'unarchive',
            tooltip: 'Unarchive tool',
            onClick: () => Backend.toggleMachineTypeArchive(user.UID, rowData.id, rowData.name, false),
          }),
        ]}
        errorMsg="Action failed. Double check you aren't using a duplicate tool name."
      />
      </ResourceTabs>

      {machineTypeToArchive && <ArchiveResourceDialog
        label="tool"
        name={machineTypeToArchive.name}
        loadAffected={loadAffected}
        onArchive={() => Backend.toggleMachineTypeArchive(user.UID, machineTypeToArchive.id, machineTypeToArchive.name, true)}
        onClose={() => setMachineTypeToArchive(null)}
        onArchived={() => {
          returnFocusRef.current = activeTabRef.current;
          setMachineTypeToArchive(null);
          setRefreshToggle(value => !value);
        }}
        finalFocusRef={returnFocusRef}
        warning="Tools are shared across spaces. Archiving a tool affects its tool instances in every space."
        affectedDescription="Instances of this tool in this space"
        emptyDescription="No instances of this tool are currently in this space."
      />}
    </Slider>
  );
}

// PropTypes for type-checking component props
ManageMachineTypesTab.propTypes = {
  user: PropTypes.object.isRequired,
  labId: PropTypes.string.isRequired,
  pushTab: PropTypes.func.isRequired,
  revert: PropTypes.func.isRequired,
};

export default ManageMachineTypesTab;
